import React from "react";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import { Button } from "@mui/material";
import useCreateExpense from "hooks/useCreateExpense";
import { useTranslation } from "react-i18next";
import TransactionsType from "enums/TransactionsType";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import { titleCase } from "title-case";
import { HugeiconsIcon } from "@hugeicons/react";
import { Car03Icon } from "@hugeicons-pro/core-stroke-rounded";
import Summary, { SummaryItem } from "../../common/Summary";
import StaticMapsImage from "components/admin/common/StaticMapsImage";
import dayjs from "utils/dayjs";
import TransactionStatus from "enums/TransactionStatus";
import ReimbursementDrawer from "components/common/reimbursements/ReimbursementDrawer";
import ApprovalDetails from "components/common/ApprovalDetails";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";
import { formatCashFull } from "utils/formatCash";
import useGetLoggedInUser from "hooks/useGetLoggedInUser";
import { formatAddressDisplay } from "hooks/useAddressData";
import { Avatar, ConfirmDialog, toast } from "@outpave/ds";
import SectionGlyph from "components/common/SectionGlyph";
import Typography from "@mui/material/Typography";
import { RenderTransactionStatus } from "components/common/RenderTransactionStatus";
import { RenderTransactionTypeChip } from "components/common/RenderTransactionType";
import { getDomain, logoLink } from "components/common/RenderMerchantDisplay";
import { Inline, Stack } from "components/layout/reusableLayouts";
import CustomPanel from "components/common/CustomPanel";
import BankAccountBanner from "components/common/reimbursements/BankAccountBanner";
import PersonalBankAccountDrawer from "components/common/users/PersonalBankAccountDrawer";
import { SendUserBankAccountInviteModal } from "components/common/users/UserModules";
import {
  canAddPayeeBank,
  canInvitePayeeBank,
} from "utils/transactions/transactionActionGuards";
import { useCanManageUsers } from "hooks/useCanManageUsers";
import { fullName } from "utils/fullName";

interface ReimbursementDetailsProps {
  transaction: any;
  /** Manager and above: approving, rejecting and the payee's profile. */
  adminTools?: boolean;
  loading?: boolean;
  handleRefresh: (refresh?: boolean) => void;
  handleOpenUserProfileDrawer: (userId: string) => void;
}

const ReimbursementDetails: React.FC<ReimbursementDetailsProps> = ({
  transaction,
  adminTools = false,
  loading = false,
  handleRefresh,
  handleOpenUserProfileDrawer,
}) => {
  const { t } = useTranslation();

  // The record is the expense being edited, so the drawer mounts only while it is open or sliding out.
  const expenseDrawer = useDrawerRecord<string>();
  const [openEditWarning, setOpenEditWarning] = React.useState(false);
  const [openCancelWarning, setOpenCancelWarning] = React.useState(false);
  const [bankDrawerOpen, setBankDrawerOpen] = React.useState(false);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const transactionDrawer = useDrawerRecord<string>();

  // Editing, resubmitting and cancelling belong to whoever submitted the
  // reimbursement. An approver reviewing someone else's gets approve or deny
  // only, so the two roles cannot act on the same buttons.
  const { user: loggedInUser } = useGetLoggedInUser();
  const isSubmitter =
    !!loggedInUser?.id && loggedInUser.id === transaction?.user?.id;

  const canManageUsers = useCanManageUsers();
  const addBank =
    !!transaction && canAddPayeeBank(transaction, { isOwner: isSubmitter });
  const inviteBank =
    !!transaction &&
    canInvitePayeeBank(transaction, { isOwner: isSubmitter, canManageUsers });
  const isMileage = transaction?.type === TransactionsType.MILEAGE;
  const isCash = transaction?.type === TransactionsType.CASH;
  const workflowRequest = transaction?.workflowRequest;

  const transactions = Array.isArray(transaction?.linkedTransactions)
    ? transaction.linkedTransactions
    : [];
  const transactionItems: SummaryItem[] = transactions.map((tx: any) => ({
    title: tx.merchantData?.name || tx.merchant || t("Transaction"),
    value: tx.amount
      ? `${formatCashFull(tx.amount, tx.currency)}`
      : t("View Transaction"),
    groupBy: t("Linked Transactions"),
    onClick: () => transactionDrawer.show(tx.id),
  }));

  const {
    approvePersonalReimbursement,
    rejectPersonalReimbursement,
    cancelPersonalReimbursement,
    approveLoading,
    rejectLoading,
    cancelLoading,
  } = useCreateExpense({});

  const onApprove = async () => {
    try {
      await approvePersonalReimbursement({
        variables: {
          requestId: transaction.workflowRequest?.id,
        },
      });
      toast.success(t("Expense approved successfully!"));
      handleRefresh(true);
    } catch (err: any) {
      toast.error(t(err.message) || t("Failed to approve expense"));
    }
  };

  const onReject = async () => {
    try {
      await rejectPersonalReimbursement({
        variables: {
          requestId: transaction.workflowRequest?.id,
        },
      });
      toast.success(t("Expense rejected successfully!"));
      handleRefresh(true);
    } catch (err: any) {
      toast.error(t(err.message) || t("Failed to reject expense"));
    }
  };

  const onCancel = async () => {
    try {
      await cancelPersonalReimbursement({
        variables: {
          transactionId: transaction?.id,
          requestId: transaction.workflowRequest?.id,
        },
      });
      toast.success(t("Expense canceled successfully!"));
      handleRefresh(true);
    } catch (err: any) {
      toast.error(t(err.message) || t("Failed to cancel expense"));
    }
  };

  const handleEditClick = () => {
    if (
      transaction.workflowRequest?.approvedBy?.length > 0 ||
      transaction.workflowRequest?.rejectedBy?.length > 0
    ) {
      setOpenEditWarning(true);
    } else {
      expenseDrawer.show(transaction.id);
    }
  };

  const handleCancelClick = () => {
    setOpenCancelWarning(true);
  };

  const summaryItems: SummaryItem[] = [
    {
      title: t("Name"),
      value: fullName(transaction?.user),
      onClick:
        adminTools && transaction?.user?.id
          ? () => handleOpenUserProfileDrawer(transaction.user.id)
          : undefined,
    },
    {
      title: t("Type"),
      // The chip, matching the transaction drawer and both tables. Its label
      // comes from the renderer rather than a title-cased enum, which only
      // happened to read correctly for these two types.
      value: transaction?.type ? (
        <RenderTransactionTypeChip transactionType={transaction.type} />
      ) : null,
    },
    {
      title: t("Description"),
      fullWidth: true,
      value: transaction?.memo,
    },
    {
      title: t("Date"),
      value: dayjs(transaction?.transactionDate).format("MMMM D, YYYY"),
    },

    ...(isCash
      ? [
          {
            title: t("Merchant"),
            value:
              transaction?.merchantData?.name || transaction?.merchant || null,
          },
        ]
      : []),

    ...transactionItems,
    {
      title: t("Approval Status"),
      value: transaction?.workflowRequest?.status
        ? titleCase(
            transaction.workflowRequest.status.replace(/_/g, " ").toLowerCase(),
          )
        : null,
    },
  ];

  const tripDetailsItems: SummaryItem[] = isMileage
    ? [
        {
          title: t("From"),
          fullWidth: true,
          value: formatAddressDisplay(transaction?.mileage?.tripFrom),
        },
        {
          title: t("To"),
          fullWidth: true,
          value: formatAddressDisplay(transaction?.mileage?.tripTo),
        },
        {
          title: t("Round Trip"),
          value: transaction?.mileage?.isRoundTrip ? t("Yes") : t("No"),
        },
        {
          title: t("Distance"),
          value: `${transaction?.mileage?.miles} miles`,
        },
      ]
    : [];

  const editableStatuses = [
    TransactionStatus.PENDING,
    TransactionStatus.PENDING_BANK_ACCOUNT,
    TransactionStatus.PENDING_PAYMENT,
    TransactionStatus.DECLINED,
    TransactionStatus.CANCELED,
    TransactionStatus.INSUFFICIENT_FUNDS,
    TransactionStatus.PAYMENT_FAIL,
  ];

  const cancelableStatuses = [
    TransactionStatus.PENDING,
    TransactionStatus.PENDING_BANK_ACCOUNT,
    TransactionStatus.PENDING_PAYMENT,
    TransactionStatus.INSUFFICIENT_FUNDS,
    TransactionStatus.PAYMENT_FAIL,
  ];

  // A cash reimbursement has a merchant, the way a card charge does. Mileage
  // has no counterparty, and its `merchant` carries the literal "MILEAGE", so
  // the identity is the person being paid back rather than that placeholder.
  const merchantName = isCash
    ? transaction?.merchantData?.name || transaction?.merchant
    : undefined;
  const payee = fullName(transaction?.user);
  const identity = merchantName || payee || "\u2014";
  // Guard on the DOMAIN, not the raw url: `getDomain` returns "" for anything
  // it cannot parse, and an empty domain builds a link with a hole in it.
  const merchantDomain = transaction?.merchantData?.url
    ? getDomain(transaction.merchantData.url)
    : "";
  const merchantLogo = merchantDomain
    ? logoLink(merchantDomain, "icon", "lettermark", "light", 64)
    : undefined;

  return (
    <>
      <Stack gap="var(--space-5)">
        <Inline justify="between" align="center">
          <Inline align="center" gap="var(--space-3)">
            <Avatar
              variant="rounded"
              size="lg"
              src={merchantLogo}
              name={identity}
              alt={identity}
            />
            <Typography variant="h6">{identity}</Typography>
          </Inline>
          <RenderTransactionStatus
            transactionStatus={transaction?.status}
            workflowStatus={transaction?.workflowRequest?.status}
            transactionType={transaction?.type}
          />
        </Inline>

        <Typography variant="h2" component="p">
          {formatCashFull(transaction?.amount, transaction?.currency)}
        </Typography>

        {(addBank || inviteBank) && (
          <BankAccountBanner
            message={t(
              addBank
                ? "ReimbursementBankAccountDetailBanner"
                : "PersonalBankAccountErrorMessage",
            )}
            actionLabel={addBank ? undefined : t("Invite")}
            onAdd={() =>
              addBank ? setBankDrawerOpen(true) : setInviteOpen(true)
            }
          />
        )}

        <Summary
          disableContainer
          alignEndColumn
          items={summaryItems}
          loading={loading}
          children={
            isSubmitter &&
            editableStatuses.includes(transaction.status) && (
              <Inline gap="var(--space-4)" justify="center">
                <Button variant="primary-soft" onClick={handleEditClick}>
                  {[
                    TransactionStatus.CANCELED,
                    TransactionStatus.DECLINED,
                    TransactionStatus.INSUFFICIENT_FUNDS,
                    TransactionStatus.PAYMENT_FAIL,
                  ].includes(transaction.status)
                    ? t("Resubmit")
                    : t("Edit")}
                </Button>
                {cancelableStatuses.includes(transaction.status) && (
                  <Button variant="danger-outlined" onClick={handleCancelClick}>
                    {t("Cancel")}
                  </Button>
                )}
              </Inline>
            )
          }
        />

        <Stack gap="var(--space-5)">
          {isMileage && (
            <CustomPanel>
              <Stack sx={{ padding: "var(--space-5)", gap: "var(--space-5)" }}>
                <Inline gap="var(--space-3)">
                  <SectionGlyph aria-label={t("Trip Details")}>
                    <HugeiconsIcon icon={Car03Icon} size={16} />
                  </SectionGlyph>
                  <Typography variant="h6">{t("Trip Details")}</Typography>
                </Inline>
                <Stack gap="var(--space-5)">
                  <StaticMapsImage
                    tripFrom={transaction?.mileage?.tripFrom}
                    tripTo={transaction?.mileage?.tripTo}
                    polyline={transaction?.mileage?.polyline}
                    width="100%"
                    height="auto"
                    borderRadius="8px"
                  />
                  <Summary items={tripDetailsItems} disableContainer />
                </Stack>
              </Stack>
            </CustomPanel>
          )}

          {workflowRequest && (
            <ApprovalDetails
              workflowRequest={workflowRequest}
              onApprove={adminTools ? onApprove : undefined}
              onReject={adminTools ? onReject : undefined}
            />
          )}
        </Stack>
      </Stack>

      {expenseDrawer.record && (
        <ReimbursementDrawer
          expenseId={expenseDrawer.record}
          open={expenseDrawer.open}
          onClose={expenseDrawer.close}
          onAfterClose={expenseDrawer.afterClose}
          onSubmitted={() => handleRefresh(true)}
        />
      )}

      {transactionDrawer.record && (
        <TransactionDetailsDrawer
          open={transactionDrawer.open}
          handleCloseDrawer={transactionDrawer.close}
          onAfterClose={transactionDrawer.afterClose}
          transactionId={transactionDrawer.record}
        />
      )}

      <ConfirmDialog
        open={openEditWarning}
        title={t("Edit Reimbursement Confirmation")}
        message={t(
          "Editing this reimbursement will reset any existing approvals. Do you wish to proceed?",
        )}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={() => {
          setOpenEditWarning(false);
          expenseDrawer.show(transaction.id);
        }}
        onCancel={() => setOpenEditWarning(false)}
      />

      <ConfirmDialog
        open={openCancelWarning}
        title={t("Cancel Reimbursement Confirmation")}
        message={t("Are you sure you want to cancel this reimbursement?")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={() => {
          setOpenCancelWarning(false);
          onCancel();
        }}
        onCancel={() => setOpenCancelWarning(false)}
      />

      {isSubmitter && (
        <PersonalBankAccountDrawer
          open={bankDrawerOpen}
          handleCloseDrawer={() => {
            setBankDrawerOpen(false);
            handleRefresh(true);
          }}
        />
      )}

      {inviteBank && (
        <SendUserBankAccountInviteModal
          openSendBankAccountInviteModal={inviteOpen}
          setOpenSendBankAccountInviteModal={setInviteOpen}
          userEmail={transaction?.user?.email ?? ""}
          userId={transaction?.user?.id ?? ""}
        />
      )}

      <LoadingWithoutModal
        open={approveLoading || rejectLoading || cancelLoading}
      />
    </>
  );
};

export default ReimbursementDetails;
